/**
 * ProtectedRoute - Route guard component
 *
 * Handles authentication and organization state routing:
 * - Unauthenticated → /login
 * - Authenticated without org → /onboarding
 * - Authenticated with org → children
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../providers/AuthProvider'
import { useOrganization } from '../../providers/OrganizationProvider'
import { postLoginRedirect } from '../../lib/redirectTarget'

// =============================================================================
// Loading Spinner (brand-aligned)
// =============================================================================

function LoadingScreen() {
  const { t } = useTranslation('auth')
  return (
    <div className="min-h-dvh bg-surface-page flex items-center justify-center">
      <div className="text-center">
        {/* Animated flux ring */}
        <div className="relative w-16 h-16 mx-auto mb-4">
          <div className="absolute inset-0 border-2 border-edge rounded-full" />
          <div className="absolute inset-0 border-2 border-transparent border-t-accent rounded-full animate-spin" />
        </div>
        <p className="text-ink-muted text-sm font-light">
          {t('loading.establishing')}
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// Protected Route
// =============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode
  requireOrg?: boolean // Default true - require organization
}

export function ProtectedRoute({
  children,
  requireOrg = true,
}: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { hasOrganizations, isLoading: orgLoading } = useOrganization()

  // Show loading while checking auth state
  if (authLoading) {
    return <LoadingScreen />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Show loading while checking organizations
  if (requireOrg && orgLoading) {
    return <LoadingScreen />
  }

  // Redirect to onboarding if no organizations
  if (requireOrg && !hasOrganizations) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}

// =============================================================================
// Public Route (redirect authenticated users)
// =============================================================================

interface PublicRouteProps {
  children: React.ReactNode
  redirectTo?: string
}

export function PublicRoute({
  children,
  redirectTo = '/dashboard',
}: PublicRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (isAuthenticated) {
    // Check if there's a pending invitation token - if so, go to onboarding
    const pendingToken = localStorage.getItem('pendingInvitationToken')
    if (pendingToken) {
      return <Navigate to="/onboarding" replace />
    }

    // Honour the location ProtectedRoute remembered when it bounced this user
    // to /login, so a one-shot URL survives the detour — notably the Revolut
    // OAuth callback, whose `?code=` is the whole point of the visit (#617).
    return <Navigate to={postLoginRedirect(location.state, redirectTo)} replace />
  }

  return <>{children}</>
}

// =============================================================================
// Onboarding Route (authenticated but no org required)
// =============================================================================

interface OnboardingRouteProps {
  children: React.ReactNode
}

export function OnboardingRoute({ children }: OnboardingRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { hasOrganizations, isLoading: orgLoading } = useOrganization()

  if (authLoading || orgLoading) {
    return <LoadingScreen />
  }

  // Not authenticated → login (preserve token in redirect)
  if (!isAuthenticated) {
    // Store the invitation token separately for reliable retrieval after login
    const token = new URLSearchParams(location.search).get('token')
    if (token) {
      localStorage.setItem('pendingInvitationToken', token)
    }
    return <Navigate to="/login" replace />
  }

  // Has orgs → dashboard (unless accepting an invitation).
  // A pending invite can arrive two ways: in the URL (?token=) when the link is
  // opened while logged in, or in localStorage (pendingInvitationToken) when an
  // unauthenticated invitee was bounced through /login — the post-login redirect
  // lands here WITHOUT the URL token. Honor both; otherwise an invitee who
  // already belongs to an organization is sent straight to the dashboard and
  // never sees the accept screen.
  const hasToken =
    new URLSearchParams(location.search).has('token') ||
    !!localStorage.getItem('pendingInvitationToken')
  if (hasOrganizations && !hasToken) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export { LoadingScreen }
